import * as React from 'react';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const EDGE_TOLERANCE_PX = 1;

/**
 * `scrollHeight` alone misses lines hidden by a descendant with
 * `overflow: clip` (e.g. CMS blurb wrappers), so text line boxes below the
 * element's bottom edge also count as hidden content.
 */
function hasHiddenContent(el: HTMLElement): boolean {
  if (el.scrollHeight > el.clientHeight + EDGE_TOLERANCE_PX) return true;

  const bottom = el.getBoundingClientRect().bottom + EDGE_TOLERANCE_PX;
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = el.ownerDocument.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim()) continue;
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.height > 0 && rect.bottom > bottom) return true;
    }
  }
  return false;
}

/**
 * Tracks whether a line-clamped element hides any of its content.
 *
 * Measures only while `active` (the clamped state) and keeps the last result
 * otherwise. Starts as `true` and stays `true` when the element has no layout
 * (SSR, jsdom, `display: none`), so callers default to showing their toggle.
 */
export function useIsClamped<T extends HTMLElement = HTMLDivElement>(
  active: boolean,
) {
  const ref = React.useRef<T>(null);
  const [isClamped, setIsClamped] = React.useState(true);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active) return;

    const check = () => {
      if (el.clientHeight === 0) return;
      setIsClamped(hasHiddenContent(el));
    };
    check();

    // A clamped box keeps its size when content grows past the clamp, so
    // content changes are watched separately from size changes.
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    resizeObserver?.observe(el);
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(check);
    mutationObserver?.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [active]);

  return { ref, isClamped };
}
